import React from "react";
import { Box, Typography, useTheme, Chip } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AccountTreeIcon from "@mui/icons-material/AccountTree";

/**
 * TitleSection Component
 *
 * Reusable header section for pages with title, subtitle, commodity badge, and tier badge
 *
 * @param {Object} props
 * @param {string} props.towerName - Name of the tower (e.g., "F&B", "BPCH")
 * @param {string} props.industry - Industry name (e.g., "Food & Beverage")
 * @param {string} [props.commodity] - Optional commodity badge text
 * @param {string} [props.tierImage] - Path to tier badge image (e.g., "/images/Silver.jpg")
 * @param {string} [props.tierLabel] - Tier label text (e.g., "Silver Tier")
 * @param {React.ReactNode} [props.actions] - Optional action buttons or elements
 * @param {boolean} [props.showAI] - Whether to show AI icon chip (default: true)
 */
const TitleSection = ({
  towerName = "Product",
  industry = "Industry",
  commodity,
  tierImage,
  tierLabel,
  actions,
  showAI = true,
}) => {
  const theme = useTheme();
  const perlColors = theme.custom?.perlColors || {};
  const perlTypography = theme.custom?.perlTypography || {};
  const perlSpacing = theme.custom?.perlSpacing || {};
  return (
    <Box
      sx={{
        borderBottom: "1px solid",
        borderColor: perlColors.border,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        pb: 1,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: perlSpacing.containerSpacing,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="h6"
              sx={{
                ...perlTypography.title,
                color: perlColors.textPrimary,
              }}
            >
              {towerName} Parametric Costing
            </Typography>
            {commodity && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Box
                  component="span"
                  sx={{
                    display: "inline-block",
                    background: "#5CDB95",
                    color: "#05386B",
                    borderRadius: 1,
                    px: 1.5,
                    py: 0.5,
                    fontWeight: 600,
                    fontSize: "0.875rem",
                  }}
                >
                  {commodity}
                </Box>
                <Chip
                  icon={
                    showAI ? (
                      <AutoAwesomeIcon sx={{ fontSize: "16px !important" }} />
                    ) : (
                      <AccountTreeIcon sx={{ fontSize: "16px !important" }} />
                    )
                  }
                  label={showAI ? "AI" : ""}
                  size="small"
                  sx={{
                    height: "24px",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    bgcolor: showAI ? "rgba(92, 219, 149, 0.15)" : "rgba(8, 145, 178, 0.12)",
                    color: showAI ? "#5CDB95" : "#0e7490",
                    border: showAI ? "1px solid rgba(92, 219, 149, 0.3)" : "none",
                    "& .MuiChip-icon": {
                      color: showAI ? "#5CDB95" : "#0e7490",
                      marginLeft: "8px",
                    },
                  }}
                />
              </Box>
            )}
          </Box>
          <Typography
            variant="caption"
            sx={{
              ...perlTypography.labelSmall,
              color: perlColors.textSecondary,
              fontStyle: "italic",
            }}
          >
            Industry: {industry}
          </Typography>
        </Box>

        {/* Actions and Tier Badge */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {/* Custom Actions */}
          {actions}

          {/* Tier Badge */}
          {tierImage && tierLabel && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                px: 2,
                py: 0.75,
                borderRadius: 1.5,
                bgcolor: "rgba(192, 192, 192, 0.12)",
                border: "1px solid rgba(192, 192, 192, 0.3)",
              }}
            >
              <Box
                component="img"
                src={tierImage}
                alt={tierLabel}
                sx={{
                  width: 24,
                  height: 24,
                  objectFit: "contain",
                  borderRadius: "50%",
                }}
              />
              <Typography
                sx={{
                  fontFamily: perlTypography.family,
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  color: perlColors.textPrimary,
                }}
              >
                {tierLabel}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default TitleSection;
